export type CompressionPurpose = 'ocr' | 'logo' | 'general';

export interface CompressionOptions {
  purpose?: CompressionPurpose;
  maxDimension?: number;
  quality?: number;
}

const PRESETS: Record<CompressionPurpose, { maxDimension: number; quality: number }> = {
  ocr: { maxDimension: 1400, quality: 0.75 },
  logo: { maxDimension: 500, quality: 0.80 },
  general: { maxDimension: 1000, quality: 0.70 },
};


/**
 * Client-side utility for compressing and resizing images using HTML Canvas.
 * Supports purpose presets ('ocr' | 'logo' | 'general') and HEIC validation.
 * 
 * @param file The original image File object from input[type=file]
 * @param optionsOrMaxDim Purpose string, CompressionOptions object, or maxDimension number
 * @param qualityOverride Optional quality override (0.0 to 1.0)
 * @returns Promise resolving to a JPEG Data URL string (data:image/jpeg;base64,...)
 */
export function compressImage(
  file: File,
  optionsOrMaxDim?: CompressionPurpose | CompressionOptions | number,
  qualityOverride?: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    let maxDimension = 1200;
    let quality = 0.75;

    if (typeof optionsOrMaxDim === 'string' && PRESETS[optionsOrMaxDim]) {
      maxDimension = PRESETS[optionsOrMaxDim].maxDimension;
      quality = PRESETS[optionsOrMaxDim].quality;
    } else if (typeof optionsOrMaxDim === 'object' && optionsOrMaxDim !== null) {
      if (optionsOrMaxDim.purpose && PRESETS[optionsOrMaxDim.purpose]) {
        maxDimension = PRESETS[optionsOrMaxDim.purpose].maxDimension;
        quality = PRESETS[optionsOrMaxDim.purpose].quality;
      }
      if (typeof optionsOrMaxDim.maxDimension === 'number') {
        maxDimension = optionsOrMaxDim.maxDimension;
      }
      if (typeof optionsOrMaxDim.quality === 'number') {
        quality = optionsOrMaxDim.quality;
      }
    } else if (typeof optionsOrMaxDim === 'number') {
      maxDimension = optionsOrMaxDim;
      if (typeof qualityOverride === 'number') {
        quality = qualityOverride;
      }
    }

    const isHeic = /\.(heic|heif)$/i.test(file.name) || /image\/heic|image\/heif/i.test(file.type);
    const isImageExt = /\.(jpe?g|png|webp|heic|heif|bmp|gif)$/i.test(file.name);

    if (file.type && !file.type.startsWith('image/') && !isImageExt) {
      return reject(new Error('O arquivo selecionado não é uma imagem válida.'));
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;

      // Validação estrita de decodificação do Canvas
      if (!width || !height || width === 0 || height === 0) {
        if (isHeic) {
          return reject(new Error('O navegador não conseguiu decodificar esta foto HEIC (iPhone). Por favor, converta a imagem para JPG/PNG antes de enviar.'));
        }
        return reject(new Error('Não foi possível ler as dimensões da imagem selecionada.'));
      }

      let targetWidth = width;
      let targetHeight = height;

      // Calculate new dimensions maintaining aspect ratio
      if (targetWidth > targetHeight) {
        if (targetWidth > maxDimension) {
          targetHeight = Math.round((targetHeight * maxDimension) / targetWidth);
          targetWidth = maxDimension;
        }
      } else {
        if (targetHeight > maxDimension) {
          targetWidth = Math.round((targetWidth * maxDimension) / targetHeight);
          targetHeight = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Não foi possível inicializar o contexto 2D do Canvas.'));
      }

      // Preenche fundo branco (preserva opacidade de PNGs transparentes)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      // Desenha a imagem redimensionada
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      try {
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // Trava de Segurança Absoluta de Payload:
        // Se a string Base64 for maior que 800KB, reduz a qualidade iterativamente
        // para garantir um payload ultraleve que jamais sobrecarrega conexões móveis ou proxies.
        let currentQuality = quality;
        while (dataUrl.length > 800 * 1024 && currentQuality > 0.35) {
          currentQuality -= 0.10;
          dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        }


        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }

    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      if (isHeic) {
        return reject(new Error('Fotos no formato HEIC (iPhone) não são suportadas nativamente por este navegador. Por favor, envie uma foto em JPG ou PNG.'));
      }

      // Fallback usando FileReader se URL.createObjectURL falhar
      const reader = new FileReader();
      reader.onload = (e) => {
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          const w = fallbackImg.naturalWidth || fallbackImg.width;
          const h = fallbackImg.naturalHeight || fallbackImg.height;

          if (!w || !h) return reject(new Error('Imagem inválida.'));

          let tw = w;
          let th = h;
          if (tw > th) {
            if (tw > maxDimension) {
              th = Math.round((th * maxDimension) / tw);
              tw = maxDimension;
            }
          } else {
            if (th > maxDimension) {
              tw = Math.round((tw * maxDimension) / th);
              th = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = tw;
          canvas.height = th;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Falha ao inicializar Canvas.'));
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, tw, th);
          ctx.drawImage(fallbackImg, 0, 0, tw, th);

          try {
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (err) {
            reject(err);
          }
        };
        fallbackImg.onerror = () => reject(new Error('Erro ao carregar a imagem selecionada.'));
        fallbackImg.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo de imagem.'));
      reader.readAsDataURL(file);
    };

    img.src = objectUrl;
  });
}

/**
 * Compresses an image file and returns a compressed Blob.
 */
export async function compressImageToBlob(
  file: File,
  optionsOrMaxDim?: CompressionPurpose | CompressionOptions | number,
  qualityOverride?: number
): Promise<Blob> {
  const dataUrl = await compressImage(file, optionsOrMaxDim, qualityOverride);
  const response = await fetch(dataUrl);
  return await response.blob();
}


